import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BackButton, Button, Card, Chip, EmptyState, Input, Select, Textarea } from "./ui";
import api, { getToken } from "./api";
import PrivateDocumentImage from "./PrivateDocumentImage";
import { countryTimeZone } from "./countries";

/* -------------------------------------------------------------------------- */
/* Pasaporte PawCita — expediente de salud privado por mascota propia.        */
/* -------------------------------------------------------------------------- */

const RECORD_TYPES = [
  { value: "vacuna", label: "Vacuna", emoji: "💉" },
  { value: "consulta", label: "Consulta", emoji: "⚕️" },
  { value: "desparasitacion", label: "Desparasitación", emoji: "🌿" },
  { value: "tratamiento", label: "Tratamiento", emoji: "💊" },
  { value: "medicamento", label: "Medicamento", emoji: "💊" },
  { value: "cirugia", label: "Cirugía", emoji: "🏥" },
  { value: "examen", label: "Examen/Laboratorio", emoji: "🧪" },
  { value: "peso", label: "Peso", emoji: "⚖️" },
  { value: "otro", label: "Otro", emoji: "📋" },
];

const typeMeta = (t) => RECORD_TYPES.find((r) => r.value === t) || RECORD_TYPES[RECORD_TYPES.length - 1];

/* Normalise DD/MM/YYYY or YYYY-MM-DD -> YYYY-MM-DD (for <input type=date>). */
function toISODate(v) {
  if (!v || typeof v !== "string") return "";
  const s = v.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{1,2})[/](\d{1,2})[/](\d{4})$/);
  if (m) {
    const [, d, mo, y] = m;
    return `${y}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  return "";
}

function parseDate(v) {
  const iso = toISODate(v);
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  return isNaN(d.getTime()) ? null : d;
}

function fmtDate(v) {
  const d = parseDate(v);
  if (!d) return v || "—";
  return d.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
}

/* Friendly timestamp (e.g. "9 sept 2026") in the selected PawCita market. */
function fmtDateTime(v, countryCode = "PA") {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d.getTime())) return fmtDate(v);
  return d.toLocaleDateString("es-ES", {
    day: "numeric", month: "short", year: "numeric", timeZone: countryTimeZone(countryCode),
  });
}

function recordTitle(r) {
  return (r.vaccine_name && r.vaccine_name.trim()) || (r.title && r.title.trim()) || typeMeta(r.type).label;
}

/* Simple duplicate heuristic: same type AND (same date OR same title/vaccine). */
function isLikelyDuplicate(proposal, existing) {
  const pType = (proposal.type || "").toLowerCase();
  const pDate = toISODate(proposal.date);
  const pTitle = ((proposal.vaccine_name || proposal.title || "").toLowerCase()).trim();
  return existing.some((r) => {
    if ((r.type || "").toLowerCase() !== pType) return false;
    const rDate = toISODate(r.date);
    const rTitle = ((r.vaccine_name || r.title || "").toLowerCase()).trim();
    const dateMatch = pDate && rDate && pDate === rDate;
    const titleMatch = pTitle && rTitle && pTitle === rTitle;
    return dateMatch || titleMatch;
  });
}

/* Spanish singular/plural labels for the visit summary chips. */
const TYPE_PLURAL = {
  vacuna: ["vacuna", "vacunas"],
  consulta: ["consulta", "consultas"],
  desparasitacion: ["desparasitación", "desparasitaciones"],
  tratamiento: ["tratamiento", "tratamientos"],
  medicamento: ["medicamento", "medicamentos"],
  cirugia: ["cirugía", "cirugías"],
  examen: ["examen", "exámenes"],
  otro: ["registro", "registros"],
};

/* Auto-summary of a visit: counts by type; weight shows the value when it's the only one. */
function summarizeVisit(items) {
  const byType = {};
  for (const r of items) (byType[r.type] = byType[r.type] || []).push(r);
  const out = [];
  for (const t of RECORD_TYPES) {
    const list = byType[t.value];
    if (!list) continue;
    if (t.value === "peso") {
      const fw = list.length === 1 ? formatWeight(list[0]) : null;
      out.push({ emoji: "⚖️", text: fw || `${list.length} pesos` });
    } else {
      const [sing, plur] = TYPE_PLURAL[t.value] || [t.label, t.label];
      out.push({ emoji: t.emoji, text: `${list.length} ${list.length === 1 ? sing : plur}` });
    }
  }
  return out;
}

/* An ambiguous vaccine: the AI flagged needs_review AND we still have no clear vaccine name.
   These must NOT be saved as a generic "Vacuna" — the user must name the vaccine first. */
function isUnresolvedVaccine(p) {
  if (p.form.type !== "vacuna" || p.needsReview !== true) return false;
  const vn = (p.form.vaccine_name || "").trim();
  const t = (p.form.title || "").trim().toLowerCase();
  const hasName = vn.length > 0 || (t.length > 0 && t !== "vacuna");
  return !hasName;
}

/* An ambiguous weight: the AI flagged needs_review AND we have a value but no clear unit.
   Must NOT be saved assuming kg — the user must pick kg or lb first. */
function isUnresolvedWeight(p) {
  if (p.form.type !== "peso" || p.needsReview !== true) return false;
  const hasValue = p.form.weight_value !== "" && p.form.weight_value != null;
  return hasValue && !p.form.weight_unit;
}

/* Display a weight preserving its original unit; falls back to legacy weight_kg. */
function formatWeight(r) {
  if (r.weight_value != null && r.weight_unit) return `${r.weight_value} ${r.weight_unit}`;
  if (r.weight_value != null) return `${r.weight_value}`;
  if (r.weight_kg != null) return `${r.weight_kg} kg`;
  return null;
}

export default function Passport({ dog, countryCode = "PA", onBack, showToast }) {
  const [mode, setMode] = useState("home"); // home | digitize | review
  const [health, setHealth] = useState(null);
  const [records, setRecords] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [cartilla, setCartilla] = useState([]);
  const [cartillaViewer, setCartillaViewer] = useState(null); // group object | null
  const [loading, setLoading] = useState(true);

  const [editingHealth, setEditingHealth] = useState(false);
  const [recordEditor, setRecordEditor] = useState(null); // {record} | {prefill:{date}} | {} | null
  const [openVisitDate, setOpenVisitDate] = useState(null); // ISO date string | "" | null(closed)
  const [resumeDoc, setResumeDoc] = useState(null); // doc to resume/review after re-entering

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [h, r, d, c] = await Promise.all([
        api.passport.getHealth(dog.id),
        api.passport.records(dog.id),
        api.passport.documents(dog.id),
        api.passport.cartilla(dog.id),
      ]);
      setHealth(h);
      setRecords(r || []);
      setDocuments(d || []);
      setCartilla(c || []);
    } catch (err) {
      showToast(err.message || "No se pudo cargar el Pasaporte", "navy");
    } finally {
      setLoading(false);
    }
  }, [dog.id, showToast]);

  useEffect(() => { load(); }, [load]);

  const removeRecord = useCallback(async (r) => {
    if (!window.confirm("¿Eliminar este registro?")) return;
    try {
      await api.passport.deleteRecord(dog.id, r.id);
      showToast("Registro eliminado", "navy");
      await load();
    } catch (err) {
      showToast(err.message || "No se pudo eliminar", "navy");
    }
  }, [dog.id, showToast, load]);

  const upcoming = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return records
      .filter((r) => {
        const d = parseDate(r.next_due_date);
        return d && d >= now;
      })
      .sort((a, b) => parseDate(a.next_due_date) - parseDate(b.next_due_date));
  }, [records]);

  // Group individual records into visits by date (visual only; Mongo stays per-record).
  const visits = useMemo(() => {
    const map = new Map();
    for (const r of records) {
      const key = toISODate(r.date) || (r.date || "");
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    const arr = Array.from(map.entries()).map(([date, items]) => ({ date, items }));
    arr.sort((a, b) => {
      if (!a.date) return 1;
      if (!b.date) return -1;
      return a.date < b.date ? 1 : a.date > b.date ? -1 : 0;
    });
    return arr;
  }, [records]);

  const pendingReviewDoc = documents.find((d) => d.analysis_status === "completed" && !d.confirmed);

  if (mode === "digitize" || mode === "review") {
    return (
      <Digitize
        dog={dog}
        mode={mode}
        setMode={(m) => { if (m === "home") setResumeDoc(null); setMode(m); }}
        existingRecords={records}
        showToast={showToast}
        resumeDoc={resumeDoc}
        onDone={async () => { setResumeDoc(null); setMode("home"); await load(); }}
      />
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 pb-32 pt-4 sm:px-6" data-testid="passport-screen">
      <div className="mb-4">
        <BackButton onClick={onBack} testId="passport-back-btn" />
      </div>

      {/* Header */}
      <Card className="overflow-hidden" data-testid="passport-header">
        <div className="flex items-center gap-4 p-5">
          <img
            src={dog.image || "/dog-placeholder.svg"}
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/dog-placeholder.svg"; }}
            alt={dog.name}
            className="h-20 w-20 flex-shrink-0 rounded-2xl object-cover"
            style={{ objectPosition: "center top" }}
          />
          <div className="min-w-0">
            <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF5C5C]">🐾 Pasaporte PawCita</p>
            <h1 className="truncate font-display text-2xl font-black text-[#0B2D4D]" data-testid="passport-dog-name">
              {dog.name}
            </h1>
            <p className="mt-0.5 truncate text-sm font-semibold text-slate-500">
              {[dog.breed, dog.age != null ? `${dog.age} años` : null, dog.sex].filter(Boolean).join(" · ")}
            </p>
          </div>
        </div>
      </Card>

      {/* Primary: the original cartilla is the main artefact. */}
      <SectionHeader emoji="📄" title="Mi cartilla digital" />
      <CartillaSection
        dogId={dog.id}
        dogName={dog.name}
        groups={cartilla}
        countryCode={countryCode}
        onChanged={load}
        onView={(g) => setCartillaViewer(g)}
        showToast={showToast}
      />

      {/* Optional AI helper (beta). */}
      <SectionHeader emoji="✨" title="Organizar con IA · Beta" />
      <button
        type="button"
        onClick={() => setMode("digitize")}
        data-testid="passport-digitize-cta"
        className="w-full rounded-3xl bg-[#0B2D4D] p-5 text-left text-white shadow-[0_16px_40px_-20px_rgba(11,45,77,0.8)] transition active:translate-y-[1px]"
      >
        <p className="text-lg font-black">✨ Organizar con IA · Beta</p>
        <p className="mt-1 text-sm text-white/80">
          PawCita puede intentar identificar vacunas, pesos y tratamientos para ayudarte a organizar tu historial.
        </p>
        <p className="mt-2 text-xs font-semibold text-amber-200">Revisa siempre los datos antes de guardarlos.</p>
      </button>

      <button
        type="button"
        onClick={() => setRecordEditor({ new: true })}
        data-testid="passport-add-record-cta"
        className="mt-3 w-full rounded-3xl border border-[#67D5B5] bg-emerald-50 p-4 text-left text-[#0B2D4D] transition active:translate-y-[1px]"
      >
        <p className="text-base font-black">＋ Agregar registro</p>
        <p className="mt-1 text-sm text-slate-600">Añade una vacuna, consulta, peso u otro dato a mano.</p>
      </button>

      {pendingReviewDoc && (
        <button
          type="button"
          onClick={() => { setResumeDoc({ id: pendingReviewDoc.id, image: pendingReviewDoc.image }); setMode("digitize"); }}
          data-testid="passport-pending-review-banner"
          className="mt-4 flex w-full items-center gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-left transition active:translate-y-[1px]"
        >
          <span className="text-2xl">📸</span>
          <span className="min-w-0 flex-1">
            <span className="block font-black text-[#0B2D4D]">Tienes una cartilla lista para revisar</span>
            <span className="block text-xs font-semibold text-amber-800">Toca para revisar los registros propuestos.</span>
          </span>
          <span className="flex-shrink-0 rounded-full bg-[#0B2D4D] px-3 py-1.5 text-xs font-bold text-white">Revisar</span>
        </button>
      )}

      {loading ? (
        <p className="mt-8 text-center text-sm text-slate-400" data-testid="passport-loading">Cargando expediente…</p>
      ) : (
        <>
          {/* Health info */}
          <SectionHeader emoji="🩺" title="Información de salud" />
          <HealthCard
            health={health}
            onEdit={() => setEditingHealth(true)}
          />

          {/* Upcoming controls */}
          <SectionHeader emoji="📅" title="Próximos controles" />
          {upcoming.length === 0 ? (
            <MiniEmpty text="Sin controles próximos programados." testId="passport-upcoming-empty" />
          ) : (
            <div className="space-y-2" data-testid="passport-upcoming-list">
              {upcoming.map((r) => (
                <div key={`up-${r.id}`} className="flex items-center gap-3 rounded-2xl border border-sky-100 bg-sky-50/60 p-3" data-testid={`passport-upcoming-${r.id}`}>
                  <span className="text-xl">{typeMeta(r.type).emoji}</span>
                  <div className="min-w-0">
                    <p className="truncate font-bold text-[#0B2D4D]">{recordTitle(r)}</p>
                    <p className="text-xs font-semibold text-sky-700">{fmtDate(r.next_due_date)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Medical history grouped by visit/date */}
          <SectionHeader emoji="📁" title="Historial médico" />
          {records.length === 0 ? (
            <MiniEmpty text="Todavía no hay registros. Digitaliza tu cartilla o agrega uno a mano." testId="passport-records-empty" />
          ) : (
            <div className="space-y-2.5" data-testid="passport-records-list">
              {visits.map((v) => (
                <VisitCard key={v.date || "nodate"} visit={v} onOpen={() => setOpenVisitDate(v.date)} />
              ))}
            </div>
          )}
        </>
      )}

      {editingHealth && (
        <HealthEditor
          dogId={dog.id}
          dogName={dog.name}
          health={health}
          onClose={() => setEditingHealth(false)}
          onSaved={async () => { setEditingHealth(false); await load(); showToast("Salud actualizada 🐾", "mint"); }}
          showToast={showToast}
        />
      )}

      {openVisitDate !== null && (
        <VisitDetail
          dateKey={openVisitDate}
          records={records}
          onClose={() => setOpenVisitDate(null)}
          onEdit={(r) => setRecordEditor({ record: r })}
          onDelete={removeRecord}
          onAddToVisit={(dateKey) => setRecordEditor({ prefill: { date: dateKey } })}
        />
      )}

      {recordEditor && (
        <RecordEditor
          dogId={dog.id}
          initial={recordEditor.record || null}
          prefill={recordEditor.prefill || null}
          onClose={() => setRecordEditor(null)}
          onSaved={async () => { setRecordEditor(null); await load(); showToast("Registro guardado 🐾", "mint"); }}
          showToast={showToast}
        />
      )}

      {cartillaViewer && (
        <CartillaViewer
          dogId={dog.id}
          dogName={dog.name}
          group={cartillaViewer}
          onClose={() => setCartillaViewer(null)}
          onChanged={async () => { await load(); }}
          syncGroup={(g) => setCartillaViewer(g)}
          showToast={showToast}
        />
      )}
    </div>
  );
}

/* --------------------------------- Pieces --------------------------------- */

function SectionHeader({ emoji, title }) {
  return (
    <p className="mb-3 mt-7 flex items-center gap-2 text-xs font-black uppercase tracking-[0.16em] text-[#FF5C5C]">
      <span aria-hidden="true">{emoji}</span> {title}
    </p>
  );
}

function MiniEmpty({ text, testId }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-200 bg-white/60 p-5 text-center text-sm text-slate-500" data-testid={testId}>
      {text}
    </div>
  );
}

function HealthCard({ health, onEdit }) {
  const has = health && Object.entries(health).some(
    ([k, v]) => !["id", "dog_id", "created_at", "updated_at", "sterilized"].includes(k) && v
  );
  const stMap = { si: "Sí", no: "No", no_informado: "No informado" };
  const rows = [
    ["Esterilizado", stMap[health?.sterilized] || "No informado"],
    ["Alergias", health?.allergies],
    ["Condiciones médicas", health?.medical_conditions],
    ["Medicamentos actuales", health?.current_medications],
    ["Tipo sanguíneo", health?.blood_type],
    ["Veterinario habitual", health?.regular_vet],
    ["Última desparasitación", health?.last_deworming_date ? fmtDate(health.last_deworming_date) : ""],
    ["Próxima desparasitación", health?.next_deworming_date ? fmtDate(health.next_deworming_date) : ""],
    ["Observaciones", health?.notes],
  ];
  return (
    <Card className="p-5" data-testid="passport-health-card">
      {!health || (!has && (!health.sterilized || health.sterilized === "no_informado")) ? (
        <div className="text-center">
          <p className="text-sm text-slate-500">Aún no has completado la información de salud de tu peludito.</p>
          <Button variant="mint" className="mt-3" onClick={onEdit} testId="passport-health-add-btn">
            Completar información de salud
          </Button>
        </div>
      ) : (
        <>
          <dl className="space-y-2.5">
            {rows.map(([label, value]) => (
              <div key={label} className="flex items-start justify-between gap-4 text-sm">
                <dt className="font-semibold text-slate-500">{label}</dt>
                <dd className="max-w-[60%] text-right font-bold text-[#0B2D4D]">{value || "—"}</dd>
              </div>
            ))}
          </dl>
          <Button variant="outline" className="mt-4 w-full" onClick={onEdit} testId="passport-health-edit-btn">
            Editar información de salud
          </Button>
        </>
      )}
    </Card>
  );
}

/* --------------------------------- Modals --------------------------------- */

function VisitCard({ visit, onOpen }) {
  const items = visit.items;
  const summary = summarizeVisit(items);
  const vet = (items.find((r) => r.veterinary_name && r.veterinary_name.trim()) || {}).veterinary_name;
  const fromCartilla = items.some((r) => r.source === "cartilla");
  const dateLabel = visit.date ? fmtDate(visit.date) : "Sin fecha";
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`passport-visit-${visit.date || "nodate"}`}
      className="w-full rounded-2xl border border-slate-100 bg-white p-4 text-left shadow-sm transition active:translate-y-[1px]"
    >
      <div className="flex items-start justify-between gap-2">
        <p className="flex min-w-0 items-center gap-2 font-black text-[#0B2D4D]">
          <span aria-hidden="true">🩺</span> <span className="min-w-0 break-words">{dateLabel}</span>
        </p>
        {fromCartilla && <Chip tone="blue" className="flex-shrink-0 text-[10px]">📸 Desde cartilla</Chip>}
      </div>
      {vet && <p className="mt-0.5 break-words text-xs font-semibold text-slate-500">{vet}</p>}
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-sm font-semibold text-slate-600">
        {summary.map((s, i) => (
          <span key={i} className="whitespace-nowrap">{s.emoji} {s.text}</span>
        ))}
      </div>
      <p className="mt-3 text-sm font-black text-[#FF5C5C]">Ver detalles ›</p>
    </button>
  );
}

function visitRecordDetails(r) {
  const parts = [];
  if (r.type === "peso") { const fw = formatWeight(r); if (fw) parts.push(fw); }
  if (r.dose) parts.push(`Dosis: ${r.dose}`);
  if (r.batch_lot) parts.push(`Lote: ${r.batch_lot}`);
  if (r.veterinary_name) parts.push(`🏥 ${r.veterinary_name}`);
  if (r.next_due_date) parts.push(`Próximo: ${fmtDate(r.next_due_date)}`);
  return parts;
}

function VisitDetail({ dateKey, records, onClose, onEdit, onDelete, onAddToVisit }) {
  const items = records.filter((r) => (toISODate(r.date) || (r.date || "")) === dateKey);
  const byType = {};
  for (const r of items) (byType[r.type] = byType[r.type] || []).push(r);
  const dateLabel = dateKey ? fmtDate(dateKey) : "Sin fecha";
  return (
    <ModalShell title={`Visita · ${dateLabel}`} onClose={onClose} testId="passport-visit-modal">
      {items.length === 0 ? (
        <p className="text-sm text-slate-500">No hay registros en esta visita.</p>
      ) : (
        <div className="space-y-4">
          {RECORD_TYPES.filter((t) => byType[t.value]).map((t) => (
            <div key={t.value}>
              <p className="mb-2 text-xs font-black uppercase tracking-[0.14em] text-[#0B2D4D]">{t.emoji} {t.label}</p>
              <div className="space-y-2">
                {byType[t.value].map((r) => {
                  const details = visitRecordDetails(r);
                  return (
                    <div key={r.id} className="rounded-2xl border border-slate-100 bg-slate-50/70 p-3" data-testid={`passport-visit-record-${r.id}`}>
                      <p className="break-words font-bold leading-snug text-[#0B2D4D]">{recordTitle(r)}</p>
                      {details.length > 0 && (
                        <p className="mt-0.5 break-words text-xs font-semibold text-slate-500">{details.join(" · ")}</p>
                      )}
                      {r.notes && <p className="mt-1 break-words text-xs text-slate-500">📝 {r.notes}</p>}
                      <div className="mt-2 flex items-center justify-between gap-2">
                        {r.source === "cartilla" ? (
                          <Chip tone="blue" className="text-[10px]">📸 Desde cartilla</Chip>
                        ) : (
                          <Chip tone="mint" className="text-[10px]">✍️ Manual</Chip>
                        )}
                        <div className="flex flex-shrink-0 gap-1.5">
                          <button
                            type="button"
                            onClick={() => onEdit(r)}
                            data-testid={`passport-visit-edit-${r.id}`}
                            className="inline-flex h-8 items-center rounded-full border border-slate-200 px-3 text-xs font-bold text-[#0B2D4D] hover:bg-white"
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => onDelete(r)}
                            data-testid={`passport-visit-delete-${r.id}`}
                            className="inline-flex h-8 items-center rounded-full bg-rose-50 px-3 text-xs font-bold text-rose-700 hover:bg-rose-100"
                          >
                            Eliminar
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      <Button variant="outline" className="mt-4 w-full py-3.5" onClick={() => onAddToVisit(dateKey)} testId="passport-add-to-visit-btn">
        ＋ Agregar dato a esta visita
      </Button>
    </ModalShell>
  );
}

/* ------------------------------- Form modals ------------------------------ */

function ModalShell({ title, onClose, children, testId }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4" data-testid={testId} onClick={onClose}>
      <div
        className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-[28px] bg-white p-5 shadow-2xl sm:rounded-[28px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <p className="font-display text-xl font-black text-[#0B2D4D]">{title}</p>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-lg font-black text-slate-500" data-testid="passport-modal-close">
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function HealthEditor({ dogId, dogName, health, onClose, onSaved, showToast }) {
  const [form, setForm] = useState({
    sterilized: health?.sterilized || "no_informado",
    allergies: health?.allergies || "",
    medical_conditions: health?.medical_conditions || "",
    current_medications: health?.current_medications || "",
    blood_type: health?.blood_type || "",
    regular_vet: health?.regular_vet || "",
    notes: health?.notes || "",
    last_deworming_date: toISODate(health?.last_deworming_date) || "",
    next_deworming_date: toISODate(health?.next_deworming_date) || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      await api.passport.saveHealth(dogId, form);
      onSaved();
    } catch (err) {
      showToast(err.message || "No se pudo guardar", "navy");
      setSaving(false);
    }
  };

  return (
    <ModalShell title={`Salud de ${dogName}`} onClose={onClose} testId="passport-health-modal">
      <div className="space-y-3">
        <Select label="¿Esterilizado?" value={form.sterilized} onChange={(e) => set("sterilized", e.target.value)} testId="passport-health-sterilized">
          <option value="no_informado">No informado</option>
          <option value="si">Sí</option>
          <option value="no">No</option>
        </Select>
        <Input label="Alergias" value={form.allergies} onChange={(e) => set("allergies", e.target.value)} testId="passport-health-allergies" />
        <Input label="Condiciones médicas" value={form.medical_conditions} onChange={(e) => set("medical_conditions", e.target.value)} testId="passport-health-conditions" />
        <Input label="Medicamentos actuales" value={form.current_medications} onChange={(e) => set("current_medications", e.target.value)} testId="passport-health-medications" />
        <Input label="Tipo sanguíneo" value={form.blood_type} onChange={(e) => set("blood_type", e.target.value)} testId="passport-health-blood" />
        <Input label="Veterinario habitual" value={form.regular_vet} onChange={(e) => set("regular_vet", e.target.value)} testId="passport-health-vet" />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Última desparasitación" type="date" value={form.last_deworming_date} onChange={(e) => set("last_deworming_date", e.target.value)} testId="passport-health-lastdeworm" />
          <Input label="Próxima desparasitación" type="date" value={form.next_deworming_date} onChange={(e) => set("next_deworming_date", e.target.value)} testId="passport-health-nextdeworm" />
        </div>
        <Textarea label="Observaciones" rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} testId="passport-health-notes" />
        <Button variant="mint" className="w-full py-3.5" disabled={saving} onClick={submit} testId="passport-health-save-btn">
          {saving ? "Guardando…" : "Guardar"}
        </Button>
      </div>
    </ModalShell>
  );
}

function RecordFields({ form, set }) {
  return (
    <div className="space-y-3">
      <Select label="Tipo de registro" value={form.type} onChange={(e) => set("type", e.target.value)} testId="passport-record-type">
        {RECORD_TYPES.map((t) => (
          <option key={t.value} value={t.value}>{t.emoji} {t.label}</option>
        ))}
      </Select>
      <Input label="Título" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Ej. Vacuna antirrábica" testId="passport-record-title" />
      <div className="grid grid-cols-2 gap-3">
        <Input label="Fecha" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} testId="passport-record-date" />
        <Input label="Próximo control" type="date" value={form.next_due_date} onChange={(e) => set("next_due_date", e.target.value)} testId="passport-record-nextdue" />
      </div>
      {form.type === "vacuna" && (
        <>
          <Input label="Nombre de la vacuna" value={form.vaccine_name} onChange={(e) => set("vaccine_name", e.target.value)} testId="passport-record-vaccine" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Dosis" value={form.dose} onChange={(e) => set("dose", e.target.value)} testId="passport-record-dose" />
            <Input label="Lote" value={form.batch_lot} onChange={(e) => set("batch_lot", e.target.value)} testId="passport-record-batch" />
          </div>
        </>
      )}
      {form.type === "peso" && (
        <div className="grid grid-cols-2 gap-3">
          <Input label="Peso" type="number" step="0.1" value={form.weight_value} onChange={(e) => set("weight_value", e.target.value)} testId="passport-record-weight" />
          <Select label="Unidad" value={form.weight_unit} onChange={(e) => set("weight_unit", e.target.value)} testId="passport-record-weight-unit">
            <option value="">Elegir…</option>
            <option value="kg">kg</option>
            <option value="lb">lb</option>
          </Select>
        </div>
      )}
      <Input label="Veterinario" value={form.veterinary_name} onChange={(e) => set("veterinary_name", e.target.value)} testId="passport-record-vetname" />
      <Textarea label="Notas" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} testId="passport-record-notes" />
    </div>
  );
}

function emptyRecordForm(initial) {
  return {
    type: initial?.type || "vacuna",
    title: initial?.title || "",
    date: toISODate(initial?.date) || "",
    next_due_date: toISODate(initial?.next_due_date) || "",
    veterinary_name: initial?.veterinary_name || "",
    notes: initial?.notes || "",
    vaccine_name: initial?.vaccine_name || "",
    dose: initial?.dose || "",
    batch_lot: initial?.batch_lot || "",
    weight_value: initial?.weight_value != null ? String(initial.weight_value)
      : (initial?.weight_kg != null ? String(initial.weight_kg) : ""),
    weight_unit: initial?.weight_unit || (initial?.weight_kg != null ? "kg" : ""),
  };
}

function toRecordBody(form) {
  return {
    type: form.type,
    title: (form.title || "").trim() || (form.vaccine_name || "").trim() || typeMeta(form.type).label,
    date: form.date || "",
    next_due_date: form.next_due_date || "",
    veterinary_name: form.veterinary_name || "",
    notes: form.notes || "",
    vaccine_name: form.vaccine_name || "",
    dose: form.dose || "",
    batch_lot: form.batch_lot || "",
    weight_value: form.weight_value !== "" && form.weight_value != null ? Number(form.weight_value) : null,
    weight_unit: form.weight_unit || null,
    weight_kg: form.weight_unit === "kg" && form.weight_value !== "" && form.weight_value != null ? Number(form.weight_value) : null,
  };
}

function RecordEditor({ dogId, initial, prefill, onClose, onSaved, showToast }) {
  const [form, setForm] = useState(emptyRecordForm(initial || prefill));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      const body = toRecordBody(form);
      if (initial) await api.passport.updateRecord(dogId, initial.id, body);
      else await api.passport.createRecord(dogId, body);
      onSaved();
    } catch (err) {
      showToast(err.message || "No se pudo guardar el registro", "navy");
      setSaving(false);
    }
  };

  return (
    <ModalShell title={initial ? "Editar registro" : "Nuevo registro"} onClose={onClose} testId="passport-record-modal">
      <RecordFields form={form} set={set} />
      <Button variant="mint" className="mt-4 w-full py-3.5" disabled={saving} onClick={submit} testId="passport-record-save-btn">
        {saving ? "Guardando…" : "Guardar registro"}
      </Button>
    </ModalShell>
  );
}

/* Keep sharing synchronous for iOS, but bind every prepared PDF to its session
   and effect generation. Browser reload is not the privacy boundary. */
export function ShareCartillaButton({ dogId, groupId, dogName, depKey, showToast, variant, className, label, testId }) {
  const [file, setFile] = useState(null);
  const [preparing, setPreparing] = useState(true);
  const [generation, setGeneration] = useState(0);
  const scope = useRef(null);

  useEffect(() => {
    const token = getToken();
    const controller = new AbortController();
    const urls = new Set();
    const state = { active: true, token, controller, urls };
    scope.current = state;
    const valid = () => state.active && !!token && getToken() === token;
    const invalidate = () => {
      state.active = false;
      controller.abort();
      urls.forEach(url => URL.revokeObjectURL(url));
      urls.clear();
      setFile(null); setPreparing(false);
    };
    const changed = () => {
      invalidate();
      if (getToken() !== token) setGeneration(value => value + 1);
    };
    const storage = event => {
      if (event.key === null || event.key === "pawcita:token") changed();
    };
    window.addEventListener("pawcita-session-changed", changed);
    window.addEventListener("storage", storage);
    setFile(null); setPreparing(!!token);
    if (token) (async () => {
      try {
        const blob = await api.passport.pdfBlob(dogId, groupId, { signal: controller.signal });
        if (!valid()) return;
        const name = `cartilla-${dogName}.pdf`.replace(/\s+/g, "_");
        setFile({ data: new File([blob], name, { type: "application/pdf" }), state });
      } catch { /* Remain disabled; never reuse an earlier PDF. */ }
      finally { if (valid()) setPreparing(false); }
    })();
    return () => {
      window.removeEventListener("pawcita-session-changed", changed);
      window.removeEventListener("storage", storage);
      invalidate();
    };
  }, [dogId, groupId, dogName, depKey, generation]);

  const onClick = () => {
    const valid = () => file && file.state === scope.current && file.state.active &&
      file.state.token === getToken() && !file.state.controller.signal.aborted;
    if (!valid()) return;
    const download = () => {
      if (!valid()) return;
      const url = URL.createObjectURL(file.data);
      file.state.urls.add(url);
      const a = document.createElement("a");
      a.href = url; a.download = file.data.name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => {
        if (file.state.urls.delete(url)) URL.revokeObjectURL(url);
      }, 1500);
      if (showToast) showToast("PDF descargado. Puedes compartirlo desde tus archivos.", "navy");
    };
    if (navigator.canShare && navigator.canShare({ files: [file.data] })) {
      navigator.share({ files: [file.data], title: `Cartilla de ${dogName}` }).catch(e => {
        if (e && e.name === "AbortError") return;
        download(); // Recheck identity after the asynchronous share failure.
      });
    } else download();
  };

  return (
    <Button variant={variant} className={className} onClick={onClick} disabled={preparing || !file} testId={testId}>
      {preparing ? "Preparando PDF…" : label}
    </Button>
  );
}

function CartillaUploader({ dogId, groupId, onUploaded, showToast, compact }) {
  const camRef = useRef(null);
  const galRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    let gid = groupId;
    try {
      for (const file of files) {
        if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
          showToast("Formato no válido. Usa JPG, PNG o WEBP.", "navy"); continue;
        }
        if (file.size > 5 * 1024 * 1024) { showToast("Una imagen supera 5 MB.", "navy"); continue; }
        const res = await api.passport.uploadDocument(dogId, file, gid);
        gid = gid || (res && res.document_group_id); // keep multiple gallery pics in one group
      }
      await onUploaded(gid);
    } catch (err) {
      showToast(err.message || "No se pudo subir la página.", "navy");
    } finally {
      setBusy(false);
      if (camRef.current) camRef.current.value = "";
      if (galRef.current) galRef.current.value = "";
    }
  };

  return (
    <div className={compact ? "grid grid-cols-2 gap-2" : "mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2"}>
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden"
        data-testid="cartilla-camera-input" onChange={(e) => handleFiles(e.target.files)} />
      <input ref={galRef} type="file" accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp" multiple className="hidden"
        data-testid="cartilla-gallery-input" onChange={(e) => handleFiles(e.target.files)} />
      <Button variant="primary" className="py-3" disabled={busy} onClick={() => camRef.current && camRef.current.click()} testId="cartilla-camera-btn">
        {busy ? "Subiendo…" : "📸 Tomar foto"}
      </Button>
      <Button variant="outline" className="py-3" disabled={busy} onClick={() => galRef.current && galRef.current.click()} testId="cartilla-gallery-btn">
        🖼️ Elegir de mi galería
      </Button>
    </div>
  );
}

function CartillaSection({ dogId, dogName, groups, countryCode = "PA", onChanged, onView, showToast }) {
  if (!groups || groups.length === 0) {
    return (
      <Card className="p-5" data-testid="cartilla-empty">
        <p className="text-sm text-slate-600">Guarda tu cartilla en PawCita para tenerla siempre contigo.</p>
        <CartillaUploader dogId={dogId} groupId={null} showToast={showToast}
          onUploaded={async () => { await onChanged(); showToast("Página agregada 🐾", "mint"); }} />
      </Card>
    );
  }
  return (
    <div className="space-y-3" data-testid="cartilla-list">
      {groups.map((g) => (
        <Card key={g.group_id} className="p-4" data-testid={`cartilla-group-${g.group_id}`}>
          <div className="flex items-center gap-3">
            <PrivateDocumentImage src={(g.pages[0] && g.pages[0].image) || "/dog-placeholder.svg"}
              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/dog-placeholder.svg"; }}
              alt="Cartilla" className="h-16 w-16 flex-shrink-0 rounded-xl object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-black text-[#0B2D4D]">🐾 Cartilla de {dogName}</p>
              <p className="text-xs font-semibold text-slate-500">{g.page_count} {g.page_count === 1 ? "página" : "páginas"} · Actualizada {fmtDateTime(g.updated_at, countryCode)}</p>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <Button variant="mint" className="py-2.5 text-sm" onClick={() => onView(g)} testId={`cartilla-view-${g.group_id}`}>
              Ver cartilla
            </Button>
            <ShareCartillaButton dogId={dogId} groupId={g.group_id} dogName={dogName} depKey={g.updated_at}
              showToast={showToast} variant="outline" className="py-2.5 text-sm" label="📤 Compartir"
              testId={`cartilla-share-${g.group_id}`} />
          </div>
        </Card>
      ))}
    </div>
  );
}

function CartillaViewer({ dogId, dogName, group, onClose, onChanged, syncGroup, showToast }) {
  const [pages, setPages] = useState(group.pages || []);
  const [lightbox, setLightbox] = useState(null);

  const refresh = useCallback(async () => {
    await onChanged();
    try {
      const list = await api.passport.cartilla(dogId);
      const g = list.find((x) => x.group_id === group.group_id);
      if (g) { setPages(g.pages); syncGroup(g); } else { onClose(); }
    } catch { /* ignore */ }
  }, [dogId, group.group_id, onChanged, syncGroup, onClose]);

  const move = async (idx, dir) => {
    const ni = idx + dir;
    if (ni < 0 || ni >= pages.length) return;
    const prev = pages;
    const arr = [...pages];
    [arr[idx], arr[ni]] = [arr[ni], arr[idx]];
    setPages(arr); // optimistic; do NOT re-fetch pages here (would overwrite the new order)
    try {
      await api.passport.reorderCartilla(dogId, group.group_id, arr.map((p) => p.id));
      onChanged(); // sync parent list, but keep our optimistic page order in the viewer
    } catch (e) {
      setPages(prev);
      showToast("No se pudo reordenar.", "navy");
    }
  };

  const removePage = async (p) => {
    if (!window.confirm("¿Eliminar esta página?")) return;
    try { await api.passport.deleteDocument(dogId, p.id); showToast("Página eliminada", "navy"); await refresh(); }
    catch (e) { showToast(e.message || "No se pudo eliminar", "navy"); }
  };

  return (
    <ModalShell title={`Cartilla de ${dogName}`} onClose={onClose} testId="cartilla-viewer">
      <div className="space-y-3">
        {pages.map((p, idx) => (
          <div key={p.id} className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-slate-50/70 p-2" data-testid={`cartilla-page-${p.id}`}>
            <button type="button" onClick={() => setLightbox(p.image)} className="flex-shrink-0" data-testid={`cartilla-page-view-${p.id}`}>
              <PrivateDocumentImage src={p.image || "/dog-placeholder.svg"} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/dog-placeholder.svg"; }}
                alt={`Página ${idx + 1}`} className="h-20 w-20 rounded-xl object-cover" />
            </button>
            <div className="min-w-0 flex-1">
              <p className="font-bold text-[#0B2D4D]">Página {idx + 1}</p>
              <div className="mt-1 flex gap-1.5">
                <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`cartilla-up-${p.id}`}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-sm font-black text-[#0B2D4D] disabled:opacity-30">↑</button>
                <button type="button" onClick={() => move(idx, 1)} disabled={idx === pages.length - 1} data-testid={`cartilla-down-${p.id}`}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-sm font-black text-[#0B2D4D] disabled:opacity-30">↓</button>
              </div>
            </div>
            <button type="button" onClick={() => removePage(p)} data-testid={`cartilla-delete-${p.id}`}
              className="inline-flex h-8 flex-shrink-0 items-center rounded-full bg-rose-50 px-3 text-xs font-bold text-rose-700">Eliminar</button>
          </div>
        ))}
      </div>
      <p className="mb-2 mt-4 text-xs font-black uppercase tracking-[0.14em] text-[#FF5C5C]">Agregar página</p>
      <CartillaUploader dogId={dogId} groupId={group.group_id} showToast={showToast}
        onUploaded={async () => { await refresh(); showToast("Página agregada 🐾", "mint"); }} />
      <ShareCartillaButton dogId={dogId} groupId={group.group_id} dogName={dogName}
        depKey={pages.map((p) => p.id).join(",")} showToast={showToast} variant="mint"
        className="mt-4 w-full py-3.5" label="📤 Compartir / Descargar PDF" testId="cartilla-viewer-share-btn" />
      {lightbox && <ImageLightbox src={lightbox} onClose={() => setLightbox(null)} />}
    </ModalShell>
  );
}

function ImageLightbox({ src, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4" data-testid="passport-doc-lightbox" onClick={onClose}>
      <button type="button" onClick={onClose} aria-label="Cerrar" className="absolute right-4 top-4 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-2xl font-black text-white" data-testid="passport-doc-lightbox-close">
        ×
      </button>
      <PrivateDocumentImage src={src} alt="Documento" className="max-h-[85vh] w-auto max-w-full rounded-2xl object-contain" onClick={(e) => e.stopPropagation()} />
    </div>
  );
}

/* -------------------------------- Digitize -------------------------------- */

function Digitize({ dog, mode, setMode, existingRecords, showToast, onDone, resumeDoc }) {
  const [doc, setDoc] = useState(null); // {id, image}
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [illegible, setIllegible] = useState(false);
  const [analysisError, setAnalysisError] = useState(false);
  const [proposals, setProposals] = useState([]); // {form, selected, id}
  const [saving, setSaving] = useState(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const uidRef = useRef(0);

  const cleanupDoc = useCallback(async (id) => {
    if (!id) return;
    try { await api.passport.deleteDocument(dog.id, id); } catch { /* best effort */ }
  }, [dog.id]);

  const handleFile = async (fileList) => {
    const file = (fileList || [])[0];
    if (!file) return;
    const okTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!okTypes.includes(file.type) || !/\.(jpe?g|png|webp)$/i.test(file.name)) {
      setUploadError("Formato no válido. Usa JPG, PNG o WEBP.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError("La imagen supera 5 MB.");
      return;
    }
    setUploadError("");
    setUploading(true);
    try {
      const res = await api.passport.uploadDocument(dog.id, file);
      setDoc(res);
    } catch (err) {
      setUploadError(err.message || "No se pudo subir la imagen.");
    } finally {
      setUploading(false);
      if (cameraRef.current) cameraRef.current.value = "";
      if (galleryRef.current) galleryRef.current.value = "";
    }
  };

  const pollRef = useRef(null);
  const stopPoll = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const buildProposals = useCallback((records) => (records || []).map((r) => ({
    id: `p-${uidRef.current++}`,
    selected: true,
    needsReview: r.needs_review === true,
    form: {
      type: RECORD_TYPES.some((t) => t.value === r.type) ? r.type : "otro",
      title: r.title || r.vaccine_name || "",
      date: toISODate(r.date) || "",
      next_due_date: toISODate(r.next_due_date) || "",
      veterinary_name: r.veterinary_name || "",
      notes: r.notes || "",
      vaccine_name: r.vaccine_name || "",
      dose: r.dose || "",
      batch_lot: r.batch_lot || "",
      weight_value: r.weight_value != null ? String(r.weight_value)
        : (r.weight_kg != null ? String(r.weight_kg) : ""),
      weight_unit: r.weight_unit === "kg" || r.weight_unit === "lb" ? r.weight_unit : "",
    },
  })), []);

  const applyStatus = useCallback((s) => {
    if (!s) return;
    if (s.status === "completed") {
      stopPoll(); setAnalyzing(false);
      if (s.readable && (s.records || []).length) { setProposals(buildProposals(s.records)); setMode("review"); }
      else { setIllegible(true); }
    } else if (s.status === "unreadable") {
      stopPoll(); setAnalyzing(false); setIllegible(true);
    } else if (s.status === "unavailable") {
      stopPoll(); setAnalyzing(false); setAnalysisError(true);
    } else {
      // pending / processing → keep waiting, never a false error.
      setAnalyzing(true);
    }
  }, [stopPoll, buildProposals, setMode]);

  const pollOnce = useCallback(async () => {
    if (!doc) return;
    try { applyStatus(await api.passport.analysisStatus(dog.id, doc.id)); }
    catch { /* transient (e.g. offline while phone locked) — keep waiting */ }
  }, [doc, dog.id, applyStatus]);

  const analyze = async (force) => {
    if (!doc) return;
    setIllegible(false); setAnalysisError(false); setAnalyzing(true);
    try {
      const res = await api.passport.analyzeDocument(dog.id, doc.id, force);
      applyStatus(res);
      if (res.status === "processing" || res.status === "pending") {
        stopPoll();
        pollRef.current = setInterval(pollOnce, 3000);
      }
    } catch {
      setAnalyzing(false); setAnalysisError(true);
    }
  };

  // Resume analysis on mount (e.g. opened from the "cartilla lista para revisar" banner) and
  // re-check whenever the app returns to the foreground after being locked / backgrounded.
  useEffect(() => {
    if (resumeDoc && !doc) { setDoc(resumeDoc); setAnalyzing(true); }
  }, [resumeDoc, doc]);

  useEffect(() => {
    if (doc && analyzing && !pollRef.current) {
      pollOnce();
      pollRef.current = setInterval(pollOnce, 3000);
    }
    const onVis = () => { if (document.visibilityState === "visible" && analyzing) pollOnce(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { document.removeEventListener("visibilitychange", onVis); };
  }, [doc, analyzing, pollOnce]);

  useEffect(() => stopPoll, [stopPoll]);

  const retryAnalyze = () => {
    setAnalysisError(false);
    analyze(false);
  };

  const retryImage = async () => {
    await cleanupDoc(doc?.id);
    setDoc(null);
    setIllegible(false);
    setAnalysisError(false);
    setProposals([]);
  };

  const cancelAll = async () => {
    stopPoll();
    // Only delete the document if it has NOT been confirmed yet.
    await cleanupDoc(doc?.id);
    setDoc(null);
    setMode("home");
  };

  const confirm = async () => {
    const chosen = proposals.filter((p) => p.selected);
    if (!chosen.length) {
      showToast("Selecciona al menos un registro para guardar.", "navy");
      return;
    }
    if (chosen.some(isUnresolvedVaccine)) {
      showToast("Indica qué vacuna fue en los registros marcados con 🔎, o desmárcalos.", "navy");
      return;
    }
    if (chosen.some(isUnresolvedWeight)) {
      showToast("Confirma la unidad del peso (kg o lb) en los registros marcados con 🔎, o desmárcalos.", "navy");
      return;
    }
    setSaving(true);
    try {
      await api.passport.confirmRecords(dog.id, {
        source_document_id: doc.id,
        records: chosen.map((p) => toRecordBody(p.form)),
      });
      showToast("Pasaporte actualizado 🐾", "mint");
      await onDone();
    } catch (err) {
      showToast(err.message || "No se pudieron guardar los registros.", "navy");
      setSaving(false);
    }
  };

  const setProposalField = (id, k, v) =>
    setProposals((ps) => ps.map((p) => (p.id === id ? { ...p, form: { ...p.form, [k]: v } } : p)));
  const toggleProposal = (id) =>
    setProposals((ps) => ps.map((p) => (p.id === id ? { ...p, selected: !p.selected } : p)));
  const removeProposal = (id) => setProposals((ps) => ps.filter((p) => p.id !== id));
  const addManualProposal = () =>
    setProposals((ps) => [
      ...ps,
      { id: `p-${uidRef.current++}`, selected: true, form: emptyRecordForm(null) },
    ]);

  /* ---------------------------- Upload / analyze view ---------------------- */
  if (mode === "digitize") {
    return (
      <div className="mx-auto max-w-3xl px-4 pb-32 pt-4 sm:px-6" data-testid="passport-digitize-screen">
        <div className="mb-4">
          <BackButton onClick={cancelAll} testId="passport-digitize-back-btn" />
        </div>
        <SectionTitleBlock
          title="📸 Digitalizar mi cartilla"
          text={`Sube una foto de la cartilla de ${dog.name} y PawCita te ayudará a organizar la información. Nunca guardamos nada sin tu revisión.`}
        />

        {!doc ? (
          <Card className="mt-5 p-6 text-center" data-testid="passport-upload-card">
            {/* Camera-only input: forces the rear camera where supported. */}
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              data-testid="passport-camera-input"
              onChange={(e) => handleFile(e.target.files)}
            />
            {/* Gallery-only input: NO capture attribute, so iOS shows the Photos picker. */}
            <input
              ref={galleryRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
              className="hidden"
              data-testid="passport-gallery-input"
              onChange={(e) => handleFile(e.target.files)}
            />
            <p className="text-5xl">🩺</p>
            <p className="mt-3 font-display text-lg font-black text-[#0B2D4D]">Toma o elige una foto</p>
            <p className="mt-1 text-sm text-slate-500">JPG, PNG o WEBP · máximo 5 MB. Procura buena luz y que el texto sea legible.</p>
            <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Button
                variant="primary"
                className="w-full py-3.5"
                disabled={uploading}
                onClick={() => cameraRef.current && cameraRef.current.click()}
                testId="passport-camera-btn"
              >
                {uploading ? "Subiendo…" : "📸 Tomar foto"}
              </Button>
              <Button
                variant="outline"
                className="w-full py-3.5"
                disabled={uploading}
                onClick={() => galleryRef.current && galleryRef.current.click()}
                testId="passport-gallery-btn"
              >
                🖼️ Elegir de mi galería
              </Button>
            </div>
            {uploadError && <p className="mt-3 text-sm font-semibold text-rose-600" data-testid="passport-upload-error">{uploadError}</p>}
          </Card>
        ) : (
          <Card className="mt-5 p-5" data-testid="passport-analyze-card">
            <PrivateDocumentImage src={doc.image} alt="Cartilla subida" className="mb-4 max-h-[50vh] w-full rounded-2xl object-contain" data-testid="passport-uploaded-image" />
            {analyzing ? (
              <div className="rounded-2xl border border-sky-100 bg-sky-50/70 p-4 text-sm text-[#0B2D4D]" data-testid="passport-analyzing-msg">
                <p className="font-bold">PawCita está leyendo la cartilla de {dog.name}… 🐾</p>
                <p className="mt-1 text-slate-600">Puedes mantener PawCita abierta. Si sales o bloqueas tu teléfono, revisaremos el resultado cuando regreses.</p>
              </div>
            ) : analysisError ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900" data-testid="passport-analysis-error-msg">
                <p className="font-bold">No pudimos analizar la cartilla en este momento 🐾</p>
                <p className="mt-1">Intenta nuevamente en unos minutos. Tu documento sigue guardado.</p>
              </div>
            ) : illegible ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="passport-illegible-msg">
                <p className="font-bold">No pudimos leer esta cartilla con suficiente claridad 🐾</p>
                <p className="mt-1">Intenta tomar otra foto con buena iluminación, evitando reflejos y procurando que el texto sea legible.</p>
              </div>
            ) : (
              <p className="text-sm text-slate-500">
                Revisa que la foto se vea nítida. Cuando estés listo, PawCita leerá la cartilla para proponerte registros que tú confirmarás.
              </p>
            )}
            {!analyzing && (
              <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {analysisError ? (
                  <Button variant="primary" className="py-3.5" onClick={retryAnalyze} testId="passport-retry-analyze-btn">
                    Reintentar análisis
                  </Button>
                ) : !illegible ? (
                  <Button variant="primary" className="py-3.5" onClick={() => analyze(false)} testId="passport-analyze-btn">
                    Analizar cartilla
                  </Button>
                ) : null}
                <Button variant="outline" className="py-3.5" onClick={retryImage} testId="passport-retry-btn">
                  Intentar con otra imagen
                </Button>
                <Button variant="ghost" className="py-3.5 sm:col-span-2" onClick={cancelAll} testId="passport-cancel-btn">
                  Cancelar
                </Button>
              </div>
            )}
          </Card>
        )}
      </div>
    );
  }

  /* -------------------------------- Review view ---------------------------- */
  const blockedSave = proposals.some((p) => p.selected && (isUnresolvedVaccine(p) || isUnresolvedWeight(p)));
  const selectedCount = proposals.filter((p) => p.selected).length;
  return (
    <div className="mx-auto max-w-3xl px-4 pt-4 pb-[calc(env(safe-area-inset-bottom)+13rem)] sm:px-6" data-testid="passport-review-screen">
      <div className="mb-4">
        <BackButton onClick={cancelAll} testId="passport-review-back-btn" />
      </div>
      <SectionTitleBlock
        title="Esto es lo que encontramos 🐾"
        text={`Revisa y corrige la información antes de guardarla en el Pasaporte de ${dog.name}. Nada se guarda hasta que confirmes.`}
      />

      {/* Persistent original photo for side-by-side comparison */}
      {doc && (
        <Card className="mt-4 p-3" data-testid="passport-review-original">
          <p className="mb-2 text-xs font-black uppercase tracking-[0.14em] text-[#FF5C5C]">Foto original</p>
          <PrivateDocumentImage src={doc.image} alt="Cartilla original" className="max-h-[45vh] w-full rounded-2xl object-contain" data-testid="passport-review-original-img" />
        </Card>
      )}

      <datalist id="pawcita-vaccine-suggestions">
        <option value="Rabia" />
        <option value="Parvovirus" />
        <option value="Moquillo" />
        <option value="Hepatitis" />
        <option value="Leptospirosis" />
        <option value="Parainfluenza" />
        <option value="Bordetella" />
        <option value="Coronavirus" />
        <option value="Polivalente (séxtuple)" />
      </datalist>

      <div className="mt-4 space-y-4" data-testid="passport-proposals">
        {proposals.map((p, idx) => {
          const dup = isLikelyDuplicate(p.form, existingRecords);
          const unresolvedVax = isUnresolvedVaccine(p);
          const unresolvedWt = isUnresolvedWeight(p);
          return (
            <Card key={p.id} className={`p-4 ${p.selected ? "" : "opacity-60"} ${unresolvedVax || unresolvedWt ? "ring-2 ring-orange-300" : ""}`} data-testid={`passport-proposal-${idx}`}>
              <div className="mb-3 flex items-center justify-between gap-2">
                <label className="flex items-center gap-2 text-sm font-bold text-[#0B2D4D]">
                  <input
                    type="checkbox"
                    checked={p.selected}
                    onChange={() => toggleProposal(p.id)}
                    className="h-5 w-5 accent-[#67D5B5]"
                    data-testid={`passport-proposal-select-${idx}`}
                  />
                  {typeMeta(p.form.type).emoji} Incluir este registro
                </label>
                <button
                  type="button"
                  onClick={() => removeProposal(p.id)}
                  data-testid={`passport-proposal-remove-${idx}`}
                  className="inline-flex h-8 items-center rounded-full bg-rose-50 px-3 text-xs font-bold text-rose-700 hover:bg-rose-100"
                >
                  Eliminar
                </button>
              </div>
              {dup && (
                <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800" data-testid={`passport-proposal-dup-${idx}`}>
                  ⚠️ Este registro podría estar duplicado.
                </div>
              )}
              {unresolvedVax ? (
                <div className="mb-3 rounded-xl border border-orange-300 bg-orange-50 p-3" data-testid={`passport-proposal-vax-required-${idx}`}>
                  <p className="text-xs font-black text-orange-800">🔎 Revisa este dato</p>
                  <p className="mt-0.5 text-xs font-semibold text-orange-800">
                    No pudimos identificar con certeza cuál vacuna corresponde a esta marca.
                  </p>
                  <input
                    list="pawcita-vaccine-suggestions"
                    value={p.form.vaccine_name || ""}
                    onChange={(e) => {
                      setProposalField(p.id, "vaccine_name", e.target.value);
                      setProposalField(p.id, "title", e.target.value);
                    }}
                    placeholder="¿Qué vacuna fue? (obligatorio)"
                    data-testid={`passport-proposal-vax-input-${idx}`}
                    className="mt-2 w-full rounded-xl border border-orange-300 bg-white px-3 py-2.5 text-sm font-semibold text-[#0B2D4D] outline-none focus:border-orange-400"
                  />
                </div>
              ) : unresolvedWt ? (
                <div className="mb-3 rounded-xl border border-orange-300 bg-orange-50 p-3" data-testid={`passport-proposal-weight-required-${idx}`}>
                  <p className="text-xs font-black text-orange-800">🔎 Confirma la unidad del peso</p>
                  <p className="mt-0.5 text-xs font-semibold text-orange-800">
                    No pudimos determinar si el peso está en kg o lb. Elige la unidad antes de guardar.
                  </p>
                  <div className="mt-2 flex gap-2">
                    {["kg", "lb"].map((u) => (
                      <button
                        key={u}
                        type="button"
                        onClick={() => setProposalField(p.id, "weight_unit", u)}
                        data-testid={`passport-proposal-weight-${u}-${idx}`}
                        className={`flex-1 rounded-xl border px-3 py-2.5 text-sm font-bold transition ${
                          p.form.weight_unit === u
                            ? "border-[#0B2D4D] bg-[#0B2D4D] text-white"
                            : "border-orange-300 bg-white text-[#0B2D4D]"
                        }`}
                      >
                        {p.form.weight_value || "?"} {u}
                      </button>
                    ))}
                  </div>
                </div>
              ) : p.needsReview ? (
                <div className="mb-3 rounded-xl border border-orange-200 bg-orange-50 px-3 py-2 text-xs font-semibold text-orange-800" data-testid={`passport-proposal-review-${idx}`}>
                  🔎 Revisa este dato: la lectura no fue del todo clara. Confirma que coincide con la cartilla.
                </div>
              ) : null}
              <RecordFields form={p.form} set={(k, v) => setProposalField(p.id, k, v)} />
            </Card>
          );
        })}
      </div>

      <Button variant="outline" className="mt-4 w-full py-3.5" onClick={addManualProposal} testId="passport-add-manual-proposal-btn">
        ＋ Agregar un registro faltante
      </Button>

      {/* Fixed action bar pinned ABOVE the global BottomNav (never bottom-0). Mirrors the
          BottomNav's centred width so it aligns with the app shell and never spans wider. */}
      <div className="pointer-events-none fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+4.75rem)] z-30 flex justify-center px-4">
        <div
          className="pointer-events-auto w-full max-w-[480px] rounded-2xl border border-slate-200 bg-white/95 p-3 shadow-[0_-10px_30px_-12px_rgba(11,45,77,0.35)] backdrop-blur md:max-w-3xl lg:max-w-5xl"
          data-testid="passport-review-actionbar"
        >
          <p className="mb-2 text-center text-xs font-black uppercase tracking-[0.14em] text-[#0B2D4D]" data-testid="passport-selected-count">
            {selectedCount} {selectedCount === 1 ? "registro seleccionado" : "registros seleccionados"}
          </p>
          {blockedSave && (
            <p className="mb-2 rounded-xl border border-orange-200 bg-orange-50 px-3 py-2 text-center text-xs font-semibold text-orange-800" data-testid="passport-review-blocked-note">
              Corrige los datos marcados con 🔎 (vacuna o unidad de peso) o desmárcalos para poder guardar.
            </p>
          )}
          <div className="flex gap-2">
            <Button variant="ghost" className="flex-1 py-3" onClick={cancelAll} disabled={saving} testId="passport-review-cancel-btn">
              Cancelar
            </Button>
            <Button variant="mint" className="flex-[2] py-3" onClick={confirm} disabled={saving || blockedSave || selectedCount === 0} testId="passport-confirm-btn">
              {saving ? "Guardando…" : `Guardar ${selectedCount} ${selectedCount === 1 ? "registro" : "registros"}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionTitleBlock({ title, text }) {
  return (
    <div>
      <h1 className="font-display text-2xl font-black text-[#0B2D4D]">{title}</h1>
      <p className="mt-2 text-sm text-slate-500">{text}</p>
    </div>
  );
}
